import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Box, Grid, Typography } from '@material-ui/core';

import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl, forceTimezone, canAccess } from 'utils';
import { debounce, isEmpty } from 'lodash';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { useStyles } from './NocCalendar.styled';
import SearchIcon from '@material-ui/icons/Search';
import SelectedItemPopup from './SelectedItemPopup';
import { SearchField } from 'components/FormFields';
import { CalendarTimeline } from 'components/UI';
import { DashboardActions } from 'state/modules/dashboard';
import { ReservationStatusSelect } from 'components/DataFields';

import { VehicleGroupsTimezonesSelect } from 'components/AsyncFields';
import { formatUsageData, getAllReservationsFromUsages } from 'services/noc';
import { FETCH_STATE } from 'constants';

const NocCalendar = ({ history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    time_zone: moment.tz.guess(),
    keyword: [],
  });
  const [selectedItem, setSelectedItem] = useState({});
  const [currentTz, setCurrentTz] = useState(moment.tz.guess());

  const { orgType } = useContext(UserContext);
  const config = useSelector((state) => state.config?.settings);
  const dashboardSettings = config.integrations?.dashboard_settings?.noc;
  const enableSubscription = config?.core?.subscription?.enabled || false;
  const requestType = config?.request_type;
  const { endDate, startDate, fetchingVehiclesUsages } = useSelector((state) => state.dashboard);
  const { fetchedReservationSuccess } = useSelector((state) => state.reservations);
  const data = useSelector((state) => state.dashboard?.vehiclesUsages?.data);
  const pagination = useSelector((state) => state.dashboard?.vehiclesUsages?.pagination);
  const reservations = useMemo(
    () => getAllReservationsFromUsages(data, currentTz) || [],
    [data, currentTz]
  );
  const usages = useMemo(() => formatUsageData(data, currentTz) || [], [data, currentTz]);
  const items = usages?.items;
  const isLoading = fetchingVehiclesUsages === FETCH_STATE.LOADING;
  const noData = !data;

  /**
   * Get usage dates
   */
  const getUsageDates = () => {
    const daysAtStart = dashboardSettings?.usage?.days_at_start || 2;
    const daysAtEnd = dashboardSettings?.usage?.days_at_end || 2;
    const initDate = startDate || moment().startOf('day').subtract(daysAtStart, 'days');
    const lastDate = endDate || moment().endOf('day').add(daysAtEnd, 'days');
    return { startDate: initDate, endDate: lastDate };
  };

  /**
   * Close context menu with selected item
   */
  const closeContextMenu = () => {
    setSelectedItem(null);
  };

  /**
   * Get vehicles groups usages
   */
  const loadVehiclesUsages = async (newParams, startDate, endDate) => {
    const payload = {
      ...newParams,
      end_time: forceTimezone(endDate).tz(currentTz).format('YYYY-MM-DDTHH:mm:00Z'),
      start_time: forceTimezone(startDate).tz(currentTz).format('YYYY-MM-DDTHH:mm:00Z'),
    };

    if (startDate !== null && endDate !== null) {
      await dispatch(DashboardActions.changeNocCalendarDates({ startDate, endDate }));
    }

    if (payload.vehicle_group_id) {
      delete payload.parent_id;
    }

    if (!isEmpty(payload.keyword)) {
      // We transform the keyword object to an array
      payload.keyword = Object.keys(payload?.keyword)
        .map((k) => (payload?.keyword[k] ? `${k}:${payload?.keyword[k]}` : ''))
        .filter((i) => i !== '');
    }

    if (isEmpty(payload.keyword)) {
      delete payload.keyword;
    }

    dispatch(DashboardActions.fetchVehicleUsages(payload));
  };

  /**
   * Search by vehicle
   */
  const onSearch = (name, value) => {
    let filters = {
      ...params,
      page: 1,
      keyword: {
        ...params?.keyword,
        [name]: value,
      },
    };
    if (name === 'reservation_status') filters['reservation_status'] = value;
    Object.keys(filters).forEach(
      (key) => key !== 'keyword' && !filters[key] && delete filters[key]
    );
    setParams({ ...filters });

    const { startDate, endDate } = getUsageDates();
    loadVehiclesUsages(filters, startDate, endDate);
  };

  /**
   * Render filters
   */
  const renderSubheaderFilters = () => {
    return (
      <Box className={classes.subheader}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3}>
            <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
              <Grid item>
                <SearchIcon className={classes.searchIcon} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <SearchField
                  allowClear
                  margin="none"
                  label={i18n.t('dashboard.search_vehicle_group')}
                  field={{
                    onChange: onSearch,
                    name: 'vehicle_group',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {orgType !== 'msOwnerOrg' && (
            <Grid item xs={12} sm={4} md={3}>
              <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
                <Grid item>
                  <SearchIcon className={classes.searchIcon} />
                </Grid>
                <Grid item style={{ width: '100%' }}>
                  <SearchField
                    allowClear
                    margin="none"
                    label={i18n.t('dashboard.search_location')}
                    field={{
                      onChange: onSearch,
                      name: 'location',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} sm={4} md={3}>
            <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
              <Grid item>
                <SearchIcon className={classes.searchIcon} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <SearchField
                  allowClear
                  margin="none"
                  label={i18n.t('dashboard.search_vehicle')}
                  field={{
                    onChange: onSearch,
                    name: 'vehicle',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
              <Grid item>
                <SearchIcon className={classes.searchIcon} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <SearchField
                  allowClear
                  margin="none"
                  label={i18n.t('dashboard.search_user')}
                  field={{
                    onChange: onSearch,
                    name: 'user',
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <VehicleGroupsTimezonesSelect
              margin="none"
              form={{
                setFieldValue: (field, value) => setCurrentTz(value || moment.tz.guess()),
              }}
              field={{
                name: 'time_zone',
                value: currentTz || '',
              }}
              label={i18n.t('dashboard.time_zone')}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Grid container spacing={1} alignItems="flex-end" wrap="nowrap">
              <Grid item>
                <SearchIcon className={classes.searchIcon} />
              </Grid>
              <Grid item style={{ width: '100%' }}>
                <ReservationStatusSelect
                  field={{
                    name: 'reservation_status',
                    value: params.reservation_status || '',
                  }}
                  omitFields={['ADMINCANCELLED', 'USERCANCELLED', 'REJECTED']}
                  margin="none"
                  form={{ setFieldValue: onSearch }}
                  label={i18n.t('dashboard.search_period_status')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  /**
   * Handle item click
   * @param {number} id
   * @param {object} event
   */
  const onTimelineClickHandler = (id, event) => {
    const item = items.find((val) => val.id === id);
    if (id === selectedItem?.item?.key) {
      return closeContextMenu();
    }
    if (item?.status !== 'OFFLINE' && canAccess(item)) {
      setSelectedItem({
        item,
        open: true,
        menuPosition: {
          x: event.clientX,
          y: event.clientY,
        },
      });
    }
  };

  /**
   * Handle noc item double click
   * @param {number} id Id of the selected item
   */
  const onItemDoubleClick = (id) => {
    const item = items.find((val) => val.id === id);
    if (item) {
      history.push(buildUrl(`/subscriptions/${item.subscription_id}`));
    }
  };

  /**
   * Handle go to reservation
   * @param {number} id Id of the selected reservation
   */
  const goToSubscription = (item) => {
    const subscriptionId = item.subscription_id || item.subscriptionId;
    history.push(buildUrl(`/subscriptions/${subscriptionId}`));
  };

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const { startDate, endDate } = getUsageDates();
    const newParams = {
      ...params,
      page: newPage + 1,
    };
    setParams(newParams);
    loadVehiclesUsages(newParams, startDate, endDate);
  };

  const handleChangeRowsPerPage = (event) => {
    const { startDate, endDate } = getUsageDates();
    const newParams = {
      ...params,
      limit: event.target.value,
    };
    setParams(newParams);
    loadVehiclesUsages(newParams, startDate, endDate);
  };

  /**
   * Handle timeline refresh
   */
  const onRefresh = (startTime, endTime, filters) => {
    const { startDate, endDate } = getUsageDates();
    loadVehiclesUsages(filters || params, startTime || startDate.utc(), endTime || endDate.utc());
  };

  /**
   * Handle scroll event
   */
  const handleScroll = () => {
    closeContextMenu();
  };

  /**
   * Handle on key press event
   */
  const handleKeyPress = (event) => {
    if (event.keyCode === 27) {
      //Esc Key
      closeContextMenu();
    }
  };

  useEffect(() => {
    if (orgType) {
      onRefresh();
    }
  }, [orgType]); // eslint-disable-line

  useEffect(() => {
    if (fetchedReservationSuccess) {
      onRefresh();
    }
  }, [fetchedReservationSuccess]); // eslint-disable-line

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleKeyPress, true);
    };
  }, []); // eslint-disable-line

  const handleRefresh = useCallback(
    debounce((params, visibleTimeStart, visibleTimeEnd) => {
      onRefresh(moment(visibleTimeStart), moment(visibleTimeEnd), params);
    }, 1000),
    []
  );

  const handleTimeChange = (visibleDatetimeStart, visibleDatetimeEnd, updateScrollCanvas) => {
    updateScrollCanvas && updateScrollCanvas(visibleDatetimeStart, visibleDatetimeEnd);
    handleRefresh(params, visibleDatetimeStart, visibleDatetimeEnd);
  };

  return (
    <Card
      enableCollapse
      data-cy="calendar"
      headerActionType="vertical"
      onRefresh={() => onRefresh()}
      loading={isLoading}
      subheader={renderSubheaderFilters}
      title={i18n.t('dashboard.periods_title')}
    >
      <Box className={classes.container}>
        <Box className={classes.aggregatesContainer}>
          {noData ? (
            <Box m={0} p={2} display="flex" alignItems="center" justifyContent="center">
              <Loading />
            </Box>
          ) : (
            <>
              <CalendarTimeline
                org={orgType}
                tz={currentTz}
                params={params}
                endDate={endDate}
                onRefresh={onRefresh}
                startDate={startDate}
                items={items}
                settings={dashboardSettings.timeline}
                groups={usages?.groups}
                customClasses={classes}
                filteredItem={params.nocVehicleId}
                onTimeChange={handleTimeChange}
                onItemDoubleClick={onItemDoubleClick}
                onTimelineClickHandler={onTimelineClickHandler}
              />
              {data?.length === 0 ? (
                <Typography variant="h4" align="center">
                  <Box fontSize={12} py={3}>
                    {i18n.t('common.no_records_to_display')}
                  </Box>
                </Typography>
              ) : (
                <Paginator
                  component="div"
                  limit={params.limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  pagination={pagination}
                  rowsPerPageOptions={[10, 15, 20, 25, 30]}
                />
              )}
            </>
          )}
        </Box>
        {selectedItem?.open && (
          <SelectedItemPopup
            item={selectedItem.item}
            requestType={requestType}
            handleClose={closeContextMenu}
            goToSubscription={goToSubscription}
            position={selectedItem.menuPosition}
            enableSubscription={enableSubscription}
          />
        )}
      </Box>
    </Card>
  );
};

NocCalendar.propTypes = {};

export default withRouter(NocCalendar);
